import React from 'react';
import { useSEO } from '../hooks/useSEO';
import ProductHero from '../components/ProductHero';
import Workflow from '../components/Workflow';
import ScreenshotGallery from '../components/ScreenshotGallery';
import TestimonialBlock from '../components/TestimonialBlock';
import IntroVideoModal from '../components/IntroVideoModal';
import { sectorVideos } from '../videos';

const CONSTRUCTION_SHOTS = [
  { src: '/images/construction/cp-live-portfolio.jpg', caption: 'Tier-1 live portfolio view — projects at every RIBA stage.' },
  { src: '/images/construction/cp-portfolio-pmo.jpg', caption: 'Construction PMO — programme, commercial and risk in one place.' },
  { src: '/images/construction/cp-bid-pipeline.jpg', caption: 'Bid pipeline with AI-assisted qualification and scoring.' },
  { src: '/images/construction/cp-project-stages.jpg', caption: 'Stage-gated delivery — governance evidence auto-compiled per gate.' },
  { src: '/images/construction/cp-safety-rams.jpg', caption: 'Safety, RAMS and CDM 2015 evidence built into the pathway.' },
  { src: '/images/construction/cp-lifecycle.jpg', caption: 'Handover creates the operational spine — not the end of information.' },
  { src: '/images/construction/cp-executive-report.jpg', caption: 'Executive narrative report — plain English, live data.' },
];

const V2VideraConstruction = () => {
  useSEO({
    title: 'Videra Construction | Governed delivery from bid to handover',
    description: 'Videra Construction brings governance, programme, risk, evidence and handover into one structured project delivery environment for Tier-1 delivery.',
    path: '/videra/construction',
  });

  return (
    <div data-testid="v2-videra-construction">
      <ProductHero
        eyebrow="Videra Construction"
        title="Better visibility from opportunity to handover."
        body={(
          <>
            <p>Videra Construction brings project governance, programme visibility, risk, evidence, approvals and handover into a structured delivery environment.</p>
            <p>Construction creates assets that may operate for decades. Good project closeout should therefore create the beginning of the operational and lifecycle record — not the end of the information.</p>
          </>
        )}
        primary={{ label: 'Explore Videra Construction', href: 'https://demo.videra.app/explore' }}
        secondary={{ label: 'Talk to Keystone', to: '/contact' }}
        heroImage="/images/construction/cp-live-portfolio.jpg"
        heroImageAlt="Videra Construction Tier-1 live portfolio"
        testid="construction-hero"
      />

      <section className="py-16 md:py-20 bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-4">Construction journey</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 mb-10">From opportunity through operation.</h2>
          <Workflow testid="construction-workflow" steps={['Opportunity','Design','Governance','Procurement','Construction','Commission','Evidence','Handover','Operation / Lifecycle']} />
        </div>
      </section>

      <ScreenshotGallery
        eyebrow="Inside Videra Construction"
        title="Structured delivery from bid to handover."
        description="Sample screens from the vertically-integrated Tier-1 contractor environment."
        shots={CONSTRUCTION_SHOTS}
        testid="construction-gallery"
      />

      <section className="py-16 md:py-20" data-testid="construction-video">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-3">Videra Construction+ walk-through</p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 leading-tight">
              Watch the Construction+ environment in action.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              A short walk-through of the vertically-integrated Tier-1 delivery environment — bid, design, procurement, safety, delivery, handover.
            </p>
          </div>
          <IntroVideoModal
            src={sectorVideos.construction?.videoUrl}
            poster="/images/construction/cp-welcome-splash.jpg"
            kicker="Construction+"
            title="Watch the walk-through"
            testid="construction-video-modal"
          />
        </div>
      </section>

      <TestimonialBlock product="videra-construction" />
    </div>
  );
};

export default V2VideraConstruction;
